import React, { useState, useEffect } from 'react';
import { t } from '../lib/i18n';
import { listEmailDrafts, approveEmailDraft } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Mail, Check, X, Eye } from 'lucide-react';
import LegacyBanner from '../components/common/LegacyBanner';

export default function EmailDrafts({ lang, tenantId }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => { loadDrafts(); }, [tenantId]);

  async function loadDrafts() {
    setLoading(true);
    try {
      const data = await listEmailDrafts(tenantId);
      setDrafts(data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function handleApprove(id, approved) {
    try {
      await approveEmailDraft(id, approved);
      toast.success(approved ? (lang === 'pl' ? 'Zatwierdzono' : 'Approved') : (lang === 'pl' ? 'Odrzucono' : 'Rejected'));
      loadDrafts();
      setDetailOpen(false);
    } catch (err) {
      toast.error(lang === 'pl' ? 'B\u0142\u0105d' : 'Error');
    }
  }

  const statusColors = {
    draft: 'bg-muted text-muted-foreground',
    approved: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20',
    rejected: 'bg-red-400/10 text-red-400 border-red-400/20'
  };

  return (
    <div className="space-y-6" data-testid="email-drafts-page">
      <LegacyBanner
        replacement="Inbox + Composer Tier 2"
        replacementPath="/inbox"
        lang={lang}
        reason={lang === 'pl'
          ? 'Stare szkice email\u00f3w negocjacyjnych (Adam v1). Nowy Inbox ma multi-mailbox + 5 szablon\u00f3w + threading + audit.'
          : 'Old negotiation email drafts (Adam v1). New Inbox has multi-mailbox + 5 templates + threading + audit.'}
      />
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
          {t('emailDrafts', lang)}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {lang === 'pl' ? 'Szkice email\u00f3w negocjacyjnych' : 'Negotiation email drafts'}
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : drafts.length === 0 ? (
        <Card className="border border-border">
          <CardContent className="text-center py-12">
            <Mail className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              {lang === 'pl' ? 'Brak szkic\u00f3w. Wygeneruj email z negocjacji.' : 'No drafts. Generate an email from negotiations.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {drafts.map(draft => (
            <Card
              key={draft.id}
              className="border border-border hover:border-border/80 cursor-pointer transition-colors duration-150"
              onClick={() => { setSelectedDraft(draft); setDetailOpen(true); }}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-medium truncate">{draft.subject || 'No subject'}</h3>
                    <p className="text-xs text-muted-foreground">
                      {lang === 'pl' ? 'Do' : 'To'}: {draft.contractor_name || 'Unknown'}
                    </p>
                  </div>
                  <Badge variant="outline" className={`text-xs ${statusColors[draft.status] || ''}`}>
                    {draft.status === 'draft' ? t('draft', lang) : draft.status === 'approved' ? t('approved', lang) : t('rejected', lang)}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">{draft.body}</p>
                <p className="text-xs text-muted-foreground/50 font-mono mt-2">{formatDate(draft.created_at)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Draft Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
          {selectedDraft && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  {selectedDraft.subject}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-3">
                <div className="flex gap-2 text-xs text-muted-foreground">
                  <span>{lang === 'pl' ? 'Do' : 'To'}: {selectedDraft.contractor_name}</span>
                  <span>\u2022</span>
                  <span>{t('tone', lang)}: {selectedDraft.tone}</span>
                </div>

                <Separator />

                <div className="p-4 rounded-xl bg-muted/20 border border-border">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{selectedDraft.body}</p>
                </div>

                {selectedDraft.strategic_notes && (
                  <div className="p-3 rounded-xl bg-[rgba(216,176,122,0.10)] border border-[rgba(216,176,122,0.22)]">
                    <h4 className="text-xs font-medium text-accent mb-1">
                      {lang === 'pl' ? 'Notatki strategiczne' : 'Strategic Notes'}
                    </h4>
                    <p className="text-xs text-muted-foreground">{selectedDraft.strategic_notes}</p>
                  </div>
                )}

                {selectedDraft.status === 'draft' && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleApprove(selectedDraft.id, true)}
                      className="flex-1 gap-2"
                      data-testid="approve-email-button"
                    >
                      <Check className="w-4 h-4" />
                      {t('approve', lang)}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleApprove(selectedDraft.id, false)}
                      className="flex-1 gap-2"
                      data-testid="reject-email-button"
                    >
                      <X className="w-4 h-4" />
                      {t('reject', lang)}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
